// src/components/admin/Tally/TallyDashboard.js
import React, { useState, useEffect, useCallback } from 'react';
import TallyService from '../../../services/TallyService';
import { useTranslation } from "react-i18next";

const TallyDashboard = ({ schoolId, academicYearId, token, onToast, academicYear }) => {
  const { t } = useTranslation(); 
  const [summary, setSummary] = useState({
    totalFeesCollected: 0,
    otherIncome: 0,
    totalIncome: 0,
    totalExpenses: 0,
    netProfit: 0,
    year: academicYear
  });
  const [loading, setLoading] = useState(true);

  const loadSummary = useCallback(async () => {
    try {
      setLoading(true);
      const [tallyData, incomeData] = await Promise.all([
        TallyService.getTallySummary(schoolId, academicYearId, token),
        TallyService.getIncomeSummary(schoolId, academicYearId)
      ]);
      const tally = tallyData?.data || tallyData || {};
      const income = incomeData?.data || incomeData || {};
      const totalFeesCollected = Number(
        income.feeIncome ?? tally.totalFeesCollected
      ) || 0;
      const otherIncome = Number(income.otherIncome) || 0;
      const totalIncome = Number(income.totalIncome) || totalFeesCollected + otherIncome;
      setSummary({
        ...tally,
        ...income,
        totalFeesCollected,
        otherIncome,
        totalIncome,
        totalExpenses: Number(income.totalExpenses ?? tally.totalExpenses) || 0,
        netProfit: Number(income.netProfit ?? tally.netProfit) || 0,
        year: income.year || tally.year || academicYear
      });
    } catch (error) {
      console.error('Error loading tally summary:', error);
      onToast?.(t('errorLoadingSummary'), 'error');
    } finally {
      setLoading(false);
    }
  }, [schoolId, academicYearId, token, onToast, t, academicYear]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="tally-loading">
        <div className="spinner"></div>
        <p>{t('loadingTallyData')}</p>
      </div>
    );
  }

  const isProfit = summary.netProfit >= 0;
  const expenseRatio = summary.totalIncome > 0
    ? (summary.totalExpenses / summary.totalIncome) * 100
    : 0;

  return (
    <div className="tally-dashboard">
      <div className="tally-header">
        <h3>📊 {t('financialSummary')}</h3>
      </div>

      <div className="tally-cards">
        <div className="tally-card fees-card">
          <div className="tally-card-icon">💰</div>
          <div className="tally-card-content">
            <div className="tally-card-label">{t('totalFeesCollected')}</div>
            <div className="tally-card-value">{formatCurrency(summary.totalFeesCollected)}</div>
            <div className="tally-card-sub">{t('incomeCredit')}</div>
          </div>
        </div>

        <div className="tally-card income-card">
          <div className="tally-card-icon">🧾</div>
          <div className="tally-card-content">
            <div className="tally-card-label">{t('otherIncome')}</div>
            <div className="tally-card-value">{formatCurrency(summary.otherIncome)}</div>
            <div className="tally-card-sub">{t('incomeFromOtherSources')}</div>
          </div>
        </div>

        <div className="tally-card total-income-card">
          <div className="tally-card-icon">💵</div>
          <div className="tally-card-content">
            <div className="tally-card-label">{t('totalIncome')}</div>
            <div className="tally-card-value">{formatCurrency(summary.totalIncome)}</div>
            <div className="tally-card-sub">{t('feesPlusOtherIncome')}</div>
          </div>
        </div>

        <div className="tally-card expenses-card">
          <div className="tally-card-icon">💸</div>
          <div className="tally-card-content">
            <div className="tally-card-label">{t('totalExpenses')}</div>
            <div className="tally-card-value">{formatCurrency(summary.totalExpenses)}</div>
            <div className="tally-card-sub">{t('expensesDebit')}</div>
          </div>
        </div>

        <div className={`tally-card profit-card ${isProfit ? 'profit-positive' : 'profit-negative'}`}>
          <div className="tally-card-icon">{isProfit ? '📈' : '📉'}</div>
          <div className="tally-card-content">
            <div className="tally-card-label">{isProfit ? t('netProfit') : t('netLoss')}</div>
            <div className="tally-card-value">{formatCurrency(Math.abs(summary.netProfit))}</div>
            <div className="tally-card-sub">
              {isProfit ? t('revenueMinusExpenses') : t('expensesMinusRevenue')}
            </div>
          </div>
        </div>
      </div>

      <div className="tally-progress-section">
        <div className="tally-progress-bar-container">
          <div className="tally-progress-label">
            <span>{t('expenseRatio')}</span>
            <span>
              {expenseRatio.toFixed(1)}%
            </span>
          </div>
          <div className="tally-progress-bar">
            <div 
              className="tally-progress-fill" 
              style={{ 
                width: `${Math.min(expenseRatio, 100)}%`
              }}
            ></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TallyDashboard;